let context
let current

function audioContext() {
  if (!context) context = new (window.AudioContext || window.webkitAudioContext)()
  return context
}

// A message is played through Web Audio and not through an audio element on purpose. An element
// hands the recording to the phone as if it were a track, and the phone then keeps it on the lock
// screen with its own controls long after it has finished. Web Audio never offers it.
export class VoicePlayback {
  #url
  #buffer = null
  #loading = null
  #warmed = false
  #source = null
  #starting = false
  #startedAt = 0
  #offset = 0
  #onEnd
  #onPause

  constructor(url, { onEnd, onPause } = {}) {
    this.#url = url
    this.#onEnd = onEnd
    this.#onPause = onPause
  }

  get playing() {
    return Boolean(this.#source)
  }

  get duration() {
    return this.#buffer ? this.#buffer.duration : 0
  }

  get currentTime() {
    if (!this.#source) return this.#offset
    return Math.min(this.#offset + (audioContext().currentTime - this.#startedAt), this.duration)
  }

  // Two taps in a row both got this far before either had a source to show for it, and the one that
  // lost the race kept playing with nobody holding its string.
  async play() {
    if (this.#source || this.#starting) return
    this.#starting = true

    try {
      // Unlocking has to be asked for while the tap is still happening: after the first await the
      // phone no longer counts this as the person having asked for sound.
      const unlocked = audioContext().resume()
      unlocked.catch(() => {})

      await this.#load()
      await unlocked
      playExclusively(this)
      routeToMedia("playback")

      this.#source = audioContext().createBufferSource()
      this.#source.buffer = this.#buffer
      this.#source.connect(audioContext().destination)
      this.#source.onended = (event) => this.#ended(event.target)
      this.#startedAt = audioContext().currentTime
      this.#source.start(0, this.#offset)
    } finally {
      this.#starting = false
    }
  }

  pause() {
    if (!this.#source) return

    this.#offset = this.currentTime
    this.#stopSource()
    this.#onPause?.()
  }

  async seek(seconds) {
    await this.#load()
    const target = Math.min(Math.max(seconds, 0), this.duration)

    if (this.playing) {
      this.#offset = target
      this.#stopSource()
      await this.play()
    } else {
      this.#offset = target
    }
  }

  release() {
    this.#stopSource()
    this.#offset = 0
    this.#dropBuffer()
  }

  // A decoded copy is PCM: megabytes for a recording that weighs kilobytes on disk. Whoever stopped
  // playing keeps where it was, not the copy, and pays a fetch again to carry on.
  dropBuffer() {
    if (!this.#source) this.#dropBuffer()
  }

  // Only the bytes, never the decode: a decoded copy is the megabytes above, while the file itself
  // is kilobytes that the play button would otherwise wait for.
  warm() {
    if (this.#buffer || this.#loading || this.#warmed) return

    this.#warmed = true
    fetch(this.#url, { priority: "low" }).catch(() => {})
  }

  async #load() {
    if (this.#buffer) return

    this.#loading ||= fetch(this.#url)
      .then((response) => response.arrayBuffer())
      .then((bytes) => audioContext().decodeAudioData(bytes))

    this.#buffer = await this.#loading
    this.#loading = null
  }

  #dropBuffer() {
    this.#buffer = null
    this.#loading = null
  }

  #stopSource() {
    if (!this.#source) return

    const source = this.#source
    this.#source = null
    source.onended = null
    source.stop()
    source.disconnect()
    routeToMedia("auto")
  }

  #ended(source) {
    if (source !== this.#source) return

    this.#source = null
    this.#offset = 0
    routeToMedia("auto")
    this.#onEnd?.()
  }
}

// Web Audio on its own leaves the phone treating this as ambient sound, which the side switch
// silences and the ringer volume governs. An audio element never had to ask, and this file stopped
// using one.
function routeToMedia(type) {
  const session = navigator.audioSession
  if (session && session.type !== type) session.type = type
}

function playExclusively(playback) {
  if (current && current !== playback) {
    current.pause()
    current.dropBuffer()
  }
  current = playback
}
